<template>
  <div class="SkillEditor">
    <v-dialog :value="dialog" persistent max-width="900" @click:outside="$emit('close')">
      <v-card>
        <v-card-title class="text-h5 d-flex justify-space-between align-center">
          <span>Skill Editor</span>
          <v-btn
            color="primary"
            outlined
            small
            @click="openAddSkillDialog"
            :disabled="!canEdit()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add New Skill
          </v-btn>
        </v-card-title>

        <!-- Skills List -->
        <v-card-text v-if="skills.length > 0">
          <div class="SkillEditor__skills-container">
            <v-card
              v-for="skill in skills"
              :key="skill.id"
              outlined
              class="SkillEditor__skill-item mb-3"
            >
              <v-card-text class="pa-3">
                <div class="d-flex justify-space-between align-center">
                  <div class="SkillEditor__skill-info">
                    <h4 class="SkillEditor__skill-name">{{ skill.name }}</h4>
                    <p class="SkillEditor__skill-description text--secondary mb-0">
                      {{ skill.description }}
                    </p>
                  </div>
                  <div class="SkillEditor__skill-actions">
                    <v-btn
                      icon
                      small
                      @click="editSkill(skill)"
                      :disabled="!canEdit()"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="startDeleteSkill(skill)"
                      :disabled="!canEdit()"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>

      <!-- Empty State -->
      <v-card-text v-else class="text-center py-8">
        <v-icon size="64" color="grey lighten-1" class="mb-4">
          mdi-file-document-outline
        </v-icon>
        <h3 class="grey--text">No skills created yet</h3>
        <p class="grey--text">Create your first skill to get started</p>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Add/Edit Skill Dialog -->
    <skill-form-dialog
      :dialog="skillFormDialog"
      :skill="selectedSkill"
      :is-editing="isEditing"
      @close="closeSkillFormDialog"
      @save="saveSkill"
      @update="updateSkillData"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the skill "{{ skillToDelete?.name }}"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="skillActionLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import SkillFormDialog from './skill-form-dialog.vue';

const { mapGetters: customerGetters } = createNamespacedHelpers('customer');
const { mapGetters: workspacesGetters } = createNamespacedHelpers('aiWorkspace');
const { mapGetters: skillsGetters, mapActions: skillsActions } = createNamespacedHelpers('aiWorkspace/skills');

export default {
  name: 'SkillEditor',
  components: {
    SkillFormDialog,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      skillFormDialog: false,
      deleteDialog: false,
      selectedSkill: null,
      skillToDelete: null,
      isEditing: false,
    };
  },
  computed: {
    ...customerGetters({
      user: 'USER',
    }),
    ...workspacesGetters({
      workspace: 'WORKSPACE',
    }),
    ...skillsGetters({
      skills: 'SKILLS',
      skillsLoading: 'SKILLS_LOADING',
      skillActionLoading: 'SKILL_ACTION_LOADING',
    }),
  },
  methods: {
    ...skillsActions([
      'getSkills',
      'createSkill',
      'updateSkill',
      'deleteSkill',
    ]),
    canEdit() {
      return this?.user?.is_privileged;
    },
    openAddSkillDialog() {
      this.selectedSkill = null;
      this.isEditing = false;
      this.skillFormDialog = true;
    },
    editSkill(skill) {
      this.selectedSkill = { ...skill };
      this.isEditing = true;
      this.skillFormDialog = true;
    },
    startDeleteSkill(skill) {
      this.skillToDelete = skill;
      this.deleteDialog = true;
    },
    closeSkillFormDialog() {
      this.skillFormDialog = false;
      this.selectedSkill = null;
      this.isEditing = false;
    },
    async saveSkill(skillData) {
      try {
        await this.createSkill({
          workspaceId: this.workspace.id,
          ...skillData,
        });
        this.closeSkillFormDialog();
        this.$store.dispatch('notifications/pushNotification', 'Skill created successfully');
      } catch (error) {
        this.$store.dispatch('notifications/setError', 'Failed to create skill');
      }
    },
    async updateSkillData(skillData) {
      try {
        await this.updateSkill({
          workspaceId: this.workspace.id,
          skillId: this.selectedSkill.id,
          ...skillData,
        });
        this.closeSkillFormDialog();
        this.$store.dispatch('notifications/pushNotification', 'Skill updated successfully');
      } catch (error) {
        this.$store.dispatch('notifications/setError', 'Failed to update skill');
      }
    },
    async confirmDelete() {
      try {
        await this.deleteSkill({
          workspaceId: this.workspace.id,
          skillId: this.skillToDelete.id,
        });
        this.deleteDialog = false;
        this.skillToDelete = null;
        this.$store.dispatch('notifications/pushNotification', 'Skill deleted successfully');
      } catch (error) {
        this.$store.dispatch('notifications/setError', 'Failed to delete skill');
      }
    },
    async loadSkills() {
      if (this.workspace?.id) {
        try {
          await this.getSkills({
            workspaceId: this.workspace.id,
          });
        } catch (error) {
          console.error('Failed to load skills:', error);
        }
      }
    },
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.loadSkills();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './skill-editor';
</style>